<template>
  <div
    class="vs-date-picker"
    :dir="direction"
    :class="{ 'vs-has-eyebrow': eyebrow == 'true' }"
  >
    <label
      v-if="label"
      ref="datepickerlabel"
      class="vs-label"
      :class="{
        'vs-label-eyebrow': eyebrow == 'true',
        'vs-has-focus': inputElFocus,
      }"
      :for="'dateinput_' + id"
    >
      {{ label }}
    </label>
    <div
      class="vs-input-group"
      :dir="direction"
      :class="{ 'vs-eyebrow-group': eyebrow == 'true' }"
    >
      <input
        :id="'dateinput_' + id"
        ref="datepickerinput"
        v-model="dateValue"
        type="text"
        class="vs-input"
        :class="{ 'vs-error': showError && !valid }"
        :placeholder="datePlaceholder"
        :aria-describedby="
          showError && !valid ? inputAllyAlert : 'description_' + id
        "
        @blur="onblur_input()"
        @focus="onfocus_input()"
      >
      <span
        :id="'description_' + id"
        class="vs-sr-only"
      >
        date format: mm/dd/yyyy
      </span>
      <button
        ref="calendarbutton"
        type="button"
        class="vs-date-picker-btn vs-calendar-icon"
        :aria-label="isOpen ? calendarCloseLabel : calendarOpenLabel"
        @keydown="onkeydown_button($event)"
        @click="onclick_button($event)"
      />
      <InlineAlert
        v-if="showError && !valid && message"
        :id="inputAllyAlert"
        :message="message"
      />
    </div>
    <div
      :id="'id-datepicker-' + id"
      ref="datepickerdialog"
      class="vs-datepicker-dialog"
      role="dialog"
      aria-modal="true"
      :aria-label="calendarDialogAriaLabel"
      @keydown.tab="ontab_trapFocus($event)"
      @keydown.esc="close($event)"
    >
      <div class="vs-dialog-header">
        <button
          ref="prevmonth"
          type="button"
          class="vs-date-picker-btn vs-prev-month"
          :aria-label="previousMonthIconLabel"
          @click="handlePreviousMonthButton($event)"
          @keydown="handlePreviousMonthButton($event)"
        />
        <h2
          :id="'id-grid-label-' + id"
          ref="monthyear"
          class="vs-lead"
          aria-live="polite"
        >
          February 2020
        </h2>
        <button
          ref="nextmonth"
          type="button"
          class="vs-date-picker-btn vs-next-month"
          :aria-label="nextMonthIconLabel"
          @click="handleNextMonthButton($event)"
          @keydown="handleNextMonthButton($event)"
        />
      </div>
      <table
        class="vs-dates"
        role="grid"
        :aria-labelledby="'id-grid-label-' + id"
      >
        <thead>
          <tr>
            <th
              scope="col"
              abbr="Sunday"
              class="vs-text"
            >
              Su
            </th>
            <th
              scope="col"
              abbr="Monday"
              class="vs-text"
            >
              Mo
            </th>
            <th
              scope="col"
              abbr="Tuesday"
              class="vs-text"
            >
              Tu
            </th>
            <th
              scope="col"
              abbr="Wednesday"
              class="vs-text"
            >
              We
            </th>
            <th
              scope="col"
              abbr="Thursday"
              class="vs-text"
            >
              Th
            </th>
            <th
              scope="col"
              abbr="Friday"
              class="vs-text"
            >
              Fr
            </th>
            <th
              scope="col"
              abbr="Saturday"
              class="vs-text"
            >
              Sa
            </th>
          </tr>
        </thead>
        <tbody ref="datetablebody">
          <tr
            v-for="(n, rowIndex) in 6"
            :id="'calendar-row-' + rowIndex"
            :key="rowIndex"
            :ref="'calendar-row-' + rowIndex"
          >
            <td
              v-for="(n, index) in 7"
              :key="rowIndex * 7 + index"
            >
              <button
                :id="'day-' + (rowIndex * 7 + index)"
                :ref="'day-' + (rowIndex * 7 + index)"
                class="vs-date-btn"
                aria-pressed="false"
                @click="onclick_day($event)"
                @keydown="onkeydown_day($event)"
              >
                {{ days[rowIndex * 7 + index] }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="vs-dialog-controls">
        <button
          ref="dialogclosebutton"
          class="vs-btn vs-btn-primary vs-dialog-button"
          value="close"
          type="button"
          @click="handleDialogCloseButton($event)"
        >
          {{ calendarCloseText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import InlineAlert from '../../inlineAlert/InlineAlert.vue';
import { generateId } from '@/helpers/generate-id';
import { isDesktop } from '@/helpers/breakpoints';
import { trapFocus } from '@/helpers/trap-focus';
import { setDirection } from '@/helpers/direction';
import dayjs from 'dayjs';

export default {
  name: 'VsDatePicker',
  props: {
    disableFutureDates: { type: String, default: 'true' },
    id: { type: String, default: generateId().toString() },
    label: { type: String, default: '' },
    eyebrow: { type: String, default: '' },
    value: { type: String },
    calendarOpenLabel: { type: String },
    calendarCloseLabel: { type: String },
    datePlaceholder: { type: String, default: '' },
    previousMonthIconLabel: { type: String },
    nextMonthIconLabel: { type: String },
    calendarDialog: { type: String },
    calendarDialogAriaLabel: { type: String },
    calendarCloseText: { type: String },
    errorMessage: { type: String },
    errorMessageYear: { type: String },
    errorMessagePastDate: { type: String },
    direction: { type: String, default: setDirection() },
  },
  components: {
    InlineAlert,
  },
  data() {
    return {
      lastRowNode: null,
      days: [],
      showError: false,
      valid: true,
      inputElFocus: false,
      inputAllyAlert: 'input_alert_' + this.id,
      focusDay: new Date(),
      selectedDay: new Date(0, 0, 1),
      isMouseDownOnBackground: false,
      defaultValue: '',
      dayLabels: [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
      ],
      monthLabels: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ],
      buttonLabelChoose: 'Choose Date',
      buttonLabelChange: 'Change Date',
      message: '',
      isOpen: false,
    };
  },
  computed: {
    dateValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
  watch: {
    dateValue(value) {
      this.handleChange(value);
    },
  },
  created() {
    this.defaultValue = this.value;
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      this.$nextTick(() => {
        this.oninit_createDayArray();
        this.updateGrid();
        this.close(false);
      });
    },
    oninit_createDayArray() {
      for (var i = 0; i < 42; i++) {
        this.days.push('-1');
      }
      this.lastRowNode = this.$el.querySelector('#calendar-row-' + 5);
    },
    onblur_input() {
      this.showError = true;
      if (this.valid) {
        this.dateValue = dayjs(this.dateValue).format('MM/DD/YYYY');
      }
      this.inputElFocus = !!this.$refs.datepickerinput.value;
    },
    onfocus_input() {
      this.inputElFocus = true;
    },
    isSameDay(day1, day2) {
      return (
        day1.getFullYear() == day2.getFullYear() &&
        day1.getMonth() == day2.getMonth() &&
        day1.getDate() == day2.getDate()
      );
    },
    isNotSameMonth(day1, day2) {
      return (
        day1.getFullYear() != day2.getFullYear() ||
        day1.getMonth() != day2.getMonth()
      );
    },
    isFutureDay(day) {
      var today = new Date();
      today.setHours(0, 0, 0, 0);
      day.setHours(0, 0, 0, 0);
      return today < day;
    },
    convertToDate(value) {
      let date = value.split('/');
      let month = parseInt(date[0]);
      let day = parseInt(date[1]);
      let year = parseInt(date[2]);

      let convertedDate = new Date(year, month - 1, day);
      return convertedDate;
    },
    isPastYear(value) {
      let currentDate = this.convertToDate(value);
      currentDate.setHours(0, 0, 0, 0);

      var oneYear = new Date(
        new Date().setFullYear(new Date().getFullYear() - 1)
      );
      oneYear.setHours(0, 0, 0, 0);
      return oneYear >= currentDate;
    },
    isCurrentMonth(day) {
      var today = new Date();
      return (
        today.getMonth() == day.getMonth() &&
        today.getFullYear() == day.getFullYear()
      );
    },
    updateGrid() {
      var flag;
      var day = this.focusDay;
      this.$refs.monthyear.innerText =
        this.monthLabels[day.getMonth()] + ' ' + day.getFullYear();
      var firstDayOfMonth = new Date(day.getFullYear(), day.getMonth(), 1);
      var dayOfWeek = firstDayOfMonth.getDay();

      firstDayOfMonth.setDate(firstDayOfMonth.getDate() - dayOfWeek);
      if (
        this.isCurrentMonth(this.focusDay) &&
        this.disableFutureDates === 'true'
      ) {
        this.$refs.nextmonth.classList.add('vs-disabled');
      } else {
        this.$refs.nextmonth.classList.remove('vs-disabled');
      }
      var firstDay = new Date(firstDayOfMonth);

      for (var i = 0; i < this.days.length; i++) {
        flag = firstDay.getMonth() != day.getMonth();
        this.updateDate(
          i,
          flag,
          firstDay,
          this.isSameDay(firstDay, this.selectedDay)
        );
        firstDay.setDate(firstDay.getDate() + 1);

        if (i === 35) {
          if (flag) {
            this.lastRowNode.style.visibility = 'hidden';
            this.lastRowNode.style.display = 'none';
          } else {
            this.lastRowNode.style.visibility = 'visible';
            this.lastRowNode.style.display = '';
          }
        }
      }
    },
    updateDate(index, disable, day, selected) {
      var dateString = day.getDate().toString();
      if (day.getDate() <= 9) {
        dateString = '0' + dateString;
      }

      var month = day.getMonth() + 1;
      if (day.getMonth() < 9) {
        month = '0' + month;
      }
      var domNode = this.$el.querySelector('#day-' + index);
      domNode.tabIndex = -1;
      domNode.removeAttribute('aria-pressed');
      domNode.setAttribute(
        'data-date',
        day.getFullYear() + '-' + month + '-' + dateString
      );
      domNode.setAttribute('aria-label', this.buildDateAriaLabel(day));
      domNode.classList.add('vs-text');
      if (disable) {
        domNode.classList.add('vs-disabled');
        domNode.textContent = '';
      } else if (this.isFutureDay(day) && this.disableFutureDates === 'true') {
        domNode.classList.add('vs-disabled');
        domNode.classList.add('vs-future-date');
        domNode.textContent = day.getDate();
        this.days[index] = day.getDate();
      } else {
        domNode.classList.remove('vs-disabled');
        domNode.classList.remove('vs-future-date');
        domNode.textContent = day.getDate();
        this.days[index] = day.getDate();
        if (selected) {
          domNode.setAttribute('aria-pressed', 'true');
          domNode.tabIndex = 0;
        }
      }
    },
    buildDateAriaLabel(day) {
      return (
        this.dayLabels[day.getDay()] +
        ' ' +
        this.monthLabels[day.getMonth()] +
        ' ' +
        day.getDate() +
        ', ' +
        day.getFullYear()
      );
    },
    getDayFromDataDateAttribute(domNode) {
      var parts = domNode.getAttribute('data-date').split('-');
      return new Date(parts[0], parseInt(parts[1]) - 1, parts[2]);
    },
    moveFocusToDay(day) {
      var d = this.focusDay;
      this.focusDay = day;

      if (
        d.getMonth() !== this.focusDay.getMonth() ||
        d.getYear() !== this.focusDay.getYear()
      ) {
        this.updateGrid();
      }
      this.setFocusDay();
    },
    setFocusDay(flag) {
      if (typeof flag !== 'boolean') {
        flag = true;
      }

      for (var i = 0; i < this.days.length; i++) {
        var dayNode = this.$el.querySelector('#day-' + i);
        var day = this.getDayFromDataDateAttribute(dayNode);
        dayNode.tabIndex = -1;
        if (this.isSameDay(day, this.focusDay)) {
          if (
            this.disableFutureDates === 'false' ||
            (this.disableFutureDates === 'true' && !this.isFutureDay(day))
          ) {
            dayNode.tabIndex = 0;
            if (flag) {
              dayNode.focus();
            }
          }
        }
      }
    },
    open() {
      this.$refs.datepickerdialog.style.display = 'block';
      this.$refs.datepickerdialog.style.zIndex = 2;

      if (isDesktop()) {
        let labelHeight = this.$refs.datepickerlabel
          ? this.$refs.datepickerlabel.clientHeight
          : 0;
        let inputHeight = this.$refs.datepickerinput
          ? this.$refs.datepickerinput.clientHeight
          : 0;
        let topPosition = labelHeight + inputHeight + 5;

        this.$refs.datepickerdialog.style.top = `${topPosition}px`;
      }

      this.getDateFromTextBox();
      this.updateGrid();
      this.isOpen = true;
    },
    close(flag) {
      if (typeof flag !== 'boolean') {
        flag = true;
      }
      this.$refs.datepickerdialog.style.display = 'none';
      if (flag) {
        this.$refs.calendarbutton.focus();
      }
      this.isOpen = false;
    },
    moveToNextYear() {
      this.focusDay = dayjs(this.focusDay).add(1, 'year').toDate();
      this.updateGrid();
    },
    moveToPreviousYear() {
      this.focusDay = dayjs(this.focusDay).subtract(1, 'year').toDate();
      this.updateGrid();
    },
    moveToNextMonth(domNode) {
      var d = dayjs(this.focusDay).add(1, 'month').toDate();
      if (!domNode.classList.contains('vs-disabled')) {
        if (
          (!this.isFutureDay(d) && this.disableFutureDates === 'true') ||
          this.disableFutureDates === 'false'
        ) {
          this.focusDay = d;
          this.updateGrid();
        }
      } else {
        if (
          (!this.isFutureDay(d) && this.disableFutureDates === 'true') ||
          this.disableFutureDates === 'false'
        ) {
          this.moveFocusToDay(d);
        }
      }
    },
    moveToPreviousMonth() {
      this.focusDay = dayjs(this.focusDay).subtract(1, 'month').toDate();
      this.updateGrid();
    },
    moveFocusToNextDay() {
      var d = dayjs(this.focusDay).add(1, 'day').toDate();
      if (
        (!this.isFutureDay(d) && this.disableFutureDates === 'true') ||
        this.disableFutureDates === 'false'
      ) {
        this.moveFocusToDay(d);
      }
    },
    moveFocusToNextWeek() {
      var d = dayjs(this.focusDay).add(1, 'week').toDate();
      if (
        (!this.isFutureDay(d) && this.disableFutureDates === 'true') ||
        this.disableFutureDates === 'false'
      ) {
        this.moveFocusToDay(d);
      }
    },
    moveFocusToPreviousDay() {
      var d = dayjs(this.focusDay).subtract(1, 'day').toDate();
      this.moveFocusToDay(d);
    },
    moveFocusToPreviousWeek() {
      var d = dayjs(this.focusDay).subtract(1, 'week').toDate();
      this.moveFocusToDay(d);
    },
    moveFocusToFirstDayOfWeek() {
      var d = dayjs(this.focusDay).startOf('week').toDate();
      if (
        (!this.isFutureDay(d) && this.disableFutureDates === 'true') ||
        this.disableFutureDates === 'false'
      ) {
        this.moveFocusToDay(d);
      }
    },
    moveFocusToLastDayOfWeek() {
      var d = dayjs(this.focusDay).endOf('week').toDate();
      if (
        (!this.isFutureDay(d) && this.disableFutureDates === 'true') ||
        this.disableFutureDates === 'false'
      ) {
        this.moveFocusToDay(d);
      }
    },
    isDayDisabled(domNode) {
      return domNode.classList.contains('vs-disabled');
    },
    setTextBoxDate(domNode) {
      var d = this.focusDay;

      if (domNode) {
        d = this.getDayFromDataDateAttribute(domNode);
        this.days.forEach((day, index) => {
          var dayNode = this.$el.querySelector('#day-' + index);
          day === d.getDate() && day === d.getMonth()
            ? dayNode.setAttribute('aria-pressed', 'true')
            : dayNode.removeAttribute('aria-pressed');
        });
      }
      if (this.dateValue !== undefined) {
        this.dateValue = this.formattedDateString(
          d.getFullYear(),
          d.getMonth() + 1,
          d.getDate()
        );
      }
    },
    getDateFromTextBox() {
      if (this.dateValue === undefined) return;
      var parts = this.dateValue.split('/');
      var month = parseInt(parts[0]);
      var day = parseInt(parts[1]);
      var year = parseInt(parts[2]);

      if (
        parts.length === 3 &&
        Number.isInteger(month) &&
        Number.isInteger(day) &&
        Number.isInteger(year)
      ) {
        if (year < 100) {
          year = 2000 + year;
        }
        var date = new Date(year, month - 1, day);
        if (
          this.disableFutureDates === 'false' ||
          (this.disableFutureDates === 'true' && !this.isFutureDay(date))
        ) {
          this.focusDay = date;
          this.selectedDay = new Date(this.focusDay);
        } else {
          this.focusDay = new Date();
          this.selectedDay = new Date(0, 0, 1);
        }
      } else {
        this.focusDay = new Date();
        this.selectedDay = new Date(0, 0, 1);
      }
    },
    handleDialogCloseButton(event) {
      var flag = false;
      switch (event.type) {
        case 'click':
          this.close();
          flag = true;
          break;
        default:
          break;
      }
      if (flag) {
        event.stopPropagation();
        event.preventDefault();
      }
    },
    handleNextMonthButton(event) {
      var flag = false;
      switch (event.type) {
        case 'keydown':
          switch (event.key) {
            case 'Enter':
              this.moveToNextMonth(event.currentTarget);
              this.setFocusDay(false);
              flag = true;
              break;
          }
          break;
        case 'click':
          this.moveToNextMonth(event.currentTarget);
          this.setFocusDay(false);
          break;
        default:
          break;
      }
      if (flag) {
        event.stopPropagation();
        event.preventDefault();
      }
    },
    handlePreviousMonthButton(event) {
      var flag = false;
      switch (event.type) {
        case 'keydown':
          switch (event.key) {
            case 'Enter':
              this.moveToPreviousMonth();
              this.setFocusDay(false);
              flag = true;
              break;
          }
          break;
        case 'click':
          this.moveToPreviousMonth();
          this.setFocusDay(false);
          flag = true;
          break;
        default:
          break;
      }
      if (flag) {
        event.stopPropagation();
        event.preventDefault();
      }
    },
    onkeydown_day(event) {
      var flag = false;
      switch (event.key) {
        case ' ':
          this.setTextBoxDate(event.currentTarget);
          flag = true;
          break;
        case 'Enter':
          this.setTextBoxDate(event.currentTarget);
          this.close();
          flag = true;
          break;
        case 'Right':
        case 'ArrowRight':
          this.moveFocusToNextDay();
          flag = true;
          break;
        case 'Left':
        case 'ArrowLeft':
          this.moveFocusToPreviousDay();
          flag = true;
          break;
        case 'Down':
        case 'ArrowDown':
          this.moveFocusToNextWeek();
          flag = true;
          break;
        case 'Up':
        case 'ArrowUp':
          this.moveFocusToPreviousWeek();
          flag = true;
          break;
        case 'PageUp':
          this.moveToPreviousMonth();
          this.setFocusDay();
          flag = true;
          break;
        case 'PageDown':
          this.moveToNextMonth(event.currentTarget);
          this.setFocusDay();
          flag = true;
          break;
        case 'Home':
          this.moveFocusToFirstDayOfWeek();
          flag = true;
          break;
        case 'End':
          this.moveFocusToLastDayOfWeek();
          flag = true;
          break;
      }

      if (flag) {
        event.stopPropagation();
        event.preventDefault();
      }
    },
    onclick_day(event) {
      if (!this.isDayDisabled(event.currentTarget)) {
        this.setTextBoxDate(event.currentTarget);
        this.close();
      }
      event.stopPropagation();
      event.preventDefault();
    },
    onkeydown_button(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        this.open();
        this.setFocusDay();
        event.stopPropagation();
        event.preventDefault();
      }
    },
    onclick_button(event) {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
        this.setFocusDay();
      }
      event.stopPropagation();
      event.preventDefault();
    },
    onmouseup_background(event) {
      if (
        !this.$refs.calendarbutton.contains(event.target) &&
        !this.$refs.datepickerdialog.contains(event.target)
      ) {
        if (this.isOpen) {
          this.close(false);
          event.stopPropagation();
          event.preventDefault();
        }
      }
    },
    ontab_trapFocus(e) {
      const container = this.$refs.datepickerdialog;

      trapFocus(container, e);
    },
    checkValidations(value) {
      if (!this.isDateValidator(value)) {
        this.valid = false;
        this.message = this.errorMessage;

        return;
      } else if (this.isPastYear(value)) {
        this.valid = false;
        this.message = this.errorMessageYear;

        return;
      } else if (this.disableFutureDates) {
        this.valid = !this.noFutureDateValidator(value);
        this.message = this.errorMessagePastDate;

        return;
      } else if (value === '') {
        this.valid = true;

        return;
      } else {
        this.valid = true;
        this.showError = false;
        this.message = '';
      }

      return;
    },
    noFutureDateValidator(value) {
      let currentDate = this.convertToDate(value);
      return this.isFutureDay(currentDate);
    },
    isDateValidator(value) {
      let expression =
        /^(0?[1-9]|1[0-2])\/(0?[1-9]|1\d|2\d|3[01])\/(19|20)\d{2}$/;

      return expression.test(value);
    },
    formattedDateString(year, month, day) {
      return dayjs(year + '/' + month + '/' + day).format('MM/DD/YYYY');
    },
    handleChange(value) {
      this.checkValidations(value);
      this.$emit('onChange');
    },
    resetValue() {
      this.dateValue = this.defaultValue;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './datePicker';
@import '@/styles/core/types/types';
@import '@/styles/buttons/buttons';
@import '@/styles/input';
@import '@/styles/label';
@import '@/styles/accessibility';
</style>
